import GuildPresetsContract from 'decentralized/contracts/GuildPresetsContract.js';
import {clearCache} from './PresetsSearcher.js';
import QueryDecryptor from 'crypto/QueryDecryptor.js';
import Web3 from 'web3';
import EthereumTransaction from 'decentralized/transactions/EthereumTransaction.js';
import {GUILD_PRESETS} from 'decentralized/transactions/contracts.js';
import ipfs from 'decentralized/Ipfs.js';
import {ipfsHashToBytes32, bytes32ToIPFSHash} from 'utils/ipfsHash.js';
import {intersect} from 'utils/intersect.js';
import BackendQuery from 'decentralized/BackendQuery.js';
import {
    NewPreset2,
    PresetTag,
    PresetSample,
    PresetInstrumentType
} from 'decentralized/EthereumEvents.js';
import {web3} from '../web3.js';


const metamaskInstalled = typeof window.web3 !== 'undefined';
const contract = new GuildPresetsContract();
const nullBytes32 = web3.utils.padLeft('0x0', 64);

class Presets2 {
    constructor(web3, crypto, guilds) {
        this.web3 = web3;
        this.contract = new web3.eth.Contract(
            contract.abi, contract.contractAddress);
        this.crypto = crypto;
        this.guilds = guilds;
        this.queryDecryptor = new QueryDecryptor(this.crypto);
    }

    async getPresetData(preset) {
        if (typeof preset === "string") {
            return await ipfs.getJson(preset);
        }
        if (preset.guildId === 0 || preset.guildId === null ||
            preset.guildId === undefined) {
            return await ipfs.getJson(preset.contentHash);
        }
        return JSON.parse(await this.crypto.fetchAndDecrypt(preset.contentHash, preset));
    }

    async getPresetTitle(hash) {
        let query = new BackendQuery(this.contract.options.address)
            .from(NewPreset2)
            .select('encryptedName')
            .where('contentHash', hash);

        let results = await query.executeQuery();

        if (results.length === 0) {
            return undefined;
        }

        return results[0].title;
    }

    newPreset(json, title, samplerHash, instrumentType, guildId) {
        let c = contract.getContract(web3);
        clearCache();
        return new Promise(async (resolve, reject) => {
            let hash = undefined;
            let encryptedContentKey = new Array(9).fill(nullBytes32);
            let publicKey = new Array(6).fill(nullBytes32);
            if (guildId !== 0) {
                let keyData = await this.guilds.getGuildKey(guildId);
                let encryptedContent = await this.crypto.encryptRawContent(
                    JSON.stringify(json), title, keyData.rawPublicKey);
                hash = await ipfs.addJson(encryptedContent.encryptedContent)
                title = this.web3.utils.padLeft('0x' + encryptedContent.encryptedName, 64);
                encryptedContentKey = encryptedContent.encryptedContentKey;
                publicKey = keyData.publicKey;
            } else {
                title = this.web3.utils.fromAscii(title);
                hash = await ipfs.addJson(json);
            }
            samplerHash = samplerHash === undefined ?
                nullBytes32 :
                ipfsHashToBytes32(samplerHash);
            instrumentType = instrumentType === undefined ?
                nullBytes32 :
                web3.utils.fromAscii(instrumentType);
            console.log("newPreset guildId =", guildId);
            await new EthereumTransaction(
                GUILD_PRESETS,
                'newPreset',
                [
                    ipfsHashToBytes32(hash),
                    title,
                    samplerHash,
                    instrumentType,
                    guildId,
                    encryptedContentKey,
                    publicKey
                ]).execute();
            resolve(hash);
        });
    }

    tagPreset(contentHash, tags, guildId) {
        if (guildId === null || guildId === undefined) {
            guildId = 0;
        }
        let c = contract.getContract(web3);

        return new Promise(async (resolve, reject) => {
            let bytes32Tags = tags.map(
                tag => web3.utils.fromAscii(tag));

            while (bytes32Tags.length < 4) {
                bytes32Tags.push(nullBytes32);
            }

            if (bytes32Tags.length > 4) {
                bytes32Tags = bytes32Tags.slice(0, 4);
            }

            new EthereumTransaction(
                GUILD_PRESETS,
                'tagPreset',
                [
                    ipfsHashToBytes32(contentHash),
                    bytes32Tags,
                    guildId
                ])
                .execute()
                .then(() => resolve());
        });
    }

    async getTotalPresets() {
        let query = new BackendQuery(this.contract.options.address)
            .from(NewPreset2)
            .select('user')
            .select('title')
            .select('contentHash');

        let results = await query.executeQuery();
        return results.length;
    }

    async getPresets(filter, guildId) {
        return await this.getPaginatedPresets(0, filter, 1000, guildId);
    }

    async getPaginatedPresets(start, filter, size=10, guildId=0) {
        let query = new BackendQuery(this.contract.options.address)
            .from(NewPreset2)
            .select('user')
            .select('encryptedName')
            .select('contentHash')
            .select('encryptedContentKey')
            .select('publicKey')
            .select('guildId')
            .where('guildId', guildId);

        let results = await query.executeQuery();

        if (!this.isFilterEmpty(filter)) {
            results = await this.filterPresets(results, filter);
        }

        if (guildId !== 0) {
            let guildKey = await this.guilds.getGuildKey(guildId);
            let decryptedResults = [];
            for (let i in results) {
                decryptedResults.push(
                    await this.queryDecryptor
                        .decryptQueryResult(results[i], guildKey.privateKey));
            }
            results = decryptedResults;
        } else {
            results = results.map(x => ({
                ... x,
                title: this.web3.utils.toAscii('0x' + x.encryptedName),
            }));
        }

        if (results.length <= start + size) {
            return results.slice(start);
        } 
        return results.slice(start, start + size);
    }

    isFilterEmpty(filter) {
        for (let i in filter.instrumentTypes) {
            if (filter.instrumentTypes[i]) {
                return false;
            }
        }

        for (let i in filter.tags) {
            if (filter.tags[i]) {
                return false;
            }
        }

        return true;
    }

    async getPresetSampleHash(hash) {
        let query = new BackendQuery(this.contract.options.address)
            .from(PresetSample)
            .select('sampleHash')
            .where('contentHash', hash);

        let results = await query.executeQuery();

        if (results.length === 0) {
            return undefined;
        }

        return results[0].sampleHash;
    }

    async getPresetInstrumentType(hash) {
        let query = new BackendQuery(this.contract.options.address)
            .from(PresetInstrumentType)
            .select('instrumentType')
            .where('contentHash', hash);

        let results = await query.executeQuery();

        if (results.length === 0) {
            return undefined;
        }

        return results[0].instrumentType;
    }

    async getAllTags(filter, guildId=0) {
        if (this.isFilterEmpty(filter)) {
            return await this.getFilteredTags(undefined, undefined, guildId);
        }
        let presets = await this.getPresets(filter, guildId);
        let tags = [];
        for (let i in presets) {
            let presetTags = await this.getTags(presets[i].contentHash);
            if (presetTags) {
                tags = tags.concat(presetTags);
            }
        }

        return Array.from(new Set(tags));
    }

    async getFilteredTags(filterType, filterValue, guildId=0) {
        let query = new BackendQuery(this.contract.address)
            .from(PresetTag)
            .where('guildId', guildId)
            .select('tag');

        if (filterValue !== undefined) {
            query.where(filterType, filterValue);
        }

        let tags = await query.executeQuery();
        tags = tags.map(tag => tag.tag)
            .filter(tag => tag !== '');

        return Array.from(new Set(tags));
    }

    async getTags(contentHash) {
        let query = new BackendQuery(this.contract.address)
            .from(PresetTag)
            .select('tag')
            .where('contentHash', contentHash);

        let tags = await query.executeQuery();
        console.log('gettags call returned = ', tags);
        tags = tags.map(tag => tag.tag)
            .filter(tag => tag !== '');

        tags.sort();

        return Array.from(new Set(tags));
    }

    async filterPresets(presets, filter) {
        let filteredByInstrumentTypes = new Set();
        let filteredByTags = new Set();
        if (filter.instrumentTypes) {
            for (let type in filter.instrumentTypes) {
                if (!filter.instrumentTypes[type]) {
                    continue;
                }

                let query = new BackendQuery(this.contract.address)
                    .from(PresetInstrumentType)
                    .select('contentHash')
                    .where('instrumentType', type);

                let results = await query.executeQuery();

                for (let j in results) {
                    let contentHash = results[j].contentHash;
                    for (let k in presets) {
                        if (presets[k].contentHash === contentHash) {
                            filteredByInstrumentTypes.add(presets[k].contentHash);
                        }
                    }
                }
            }
        }

        if (filter.tags) {
            for (let tag in filter.tags) {
                if (!filter.tags[tag]) {
                    continue;
                }

                let query = new BackendQuery(this.contract.address)
                    .from(PresetTag)
                    .select('contentHash')
                    .where('tag', tag);

                let results = await query.executeQuery();

                for (let j in results) {
                    let contentHash = results[j].contentHash;
                    for (let k in presets) {
                        if (presets[k].contentHash === contentHash) {
                            filteredByTags.add(presets[k].contentHash);
                        }
                    }
                }
            }
        }

        if (filteredByInstrumentTypes.size === 0) {
            return this.convertToPresets(Array.from(filteredByTags), presets);
        } else if (filteredByTags.size === 0) {
            return this.convertToPresets(Array.from(filteredByInstrumentTypes), presets);
        }

        let intersected = Array.from(intersect(
            filteredByInstrumentTypes,
            filteredByTags));

        return this.convertToPresets(intersected, presets);
    }

    convertToPresets(presetIds, presets) {
        let filtered = [];
        for (let i in presetIds) {
            for (let j in presets) {
                if (presets[j].contentHash === presetIds[i]) {
                    filtered.push(presets[j]);
                }
            }
        }

        return filtered;
    }

    async getAllUserAddresses() {
        /*
        const accounts = await web3.eth.getAccounts();
        let query = new BackendQuery(this.contract.address)
            .from(PresetTag)
            .select('user');

        let users = await query.executeQuery();
        users = users.map(user => user.user);

        return Array.from(new Set(users));
        */
        return [];
    }
}

export default Presets2;
